// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useOperatorParty } from '@hooks/queries/utility-operator/useOperatorParty';
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useRequestClientOnboarderService from '../../../hooks/mutations/registry/onboarding/useRequestClientOnboarderService';
import useMutate from '../../../hooks/mutations/useMutate';
import {
  ERR_CREATE_CLIENT_ONBOARDER_SERVICE_REQUEST,
  SUC_CREATE_CLIENT_ONBOARDER_SERVICE_REQUEST,
} from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestClientOnboarderServiceDialog = ({ open, onClose }: DialogProps) => {
  const [registrar, setRegistrar] = useState<string>('');
  const [isLoading, setLoading] = useState(false);
  const [upgrader, setUpgrader] = useState<string>('');

  const operator = useOperatorParty()?.data;
  const clientOnboarder = useModuleParty();

  const requestClientOnboarderService = useRequestClientOnboarderService();
  const mutate = useMutate();

  const resetState = useCallback(() => {
    setRegistrar('');
    setLoading(false);
    setUpgrader('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const createClientOnboarderServiceRequest = useCallback(async () => {
    if (!operator) return;
    setLoading(true);
    try {
      await mutate(
        requestClientOnboarderService,
        {
          operator,
          registrar,
          clientOnboarder,
          upgrader,
        },
        SUC_CREATE_CLIENT_ONBOARDER_SERVICE_REQUEST,
        ERR_CREATE_CLIENT_ONBOARDER_SERVICE_REQUEST,
      );
    } finally {
      handleClose();
      setLoading(false);
    }
  }, [
    operator,
    registrar,
    clientOnboarder,
    upgrader,
    handleClose,
    requestClientOnboarderService,
    mutate,
  ]);

  const noRegistrarSet = registrar === '';
  const isSameParty = registrar.trim() === clientOnboarder;
  const disableSubmit = noRegistrarSet || isSameParty || !upgrader || !operator || isLoading;

  return (
    <FormDialog
      open={open}
      title="Request Client Onboarder Service"
      onCancel={handleClose}
      onSubmit={createClientOnboarderServiceRequest}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextInput
          enableTopLabel
          label="Registrar Party ID"
          value={registrar}
          setValue={setRegistrar}
          validationMessage={isSameParty ? 'Registrar must be a different party' : ''}
        />
        <TextInput
          enableTopLabel
          label="Upgrader Party ID"
          value={upgrader}
          setValue={setUpgrader}
        />
      </Box>
    </FormDialog>
  );
};

export default RequestClientOnboarderServiceDialog;
